import { useState, useRef, useEffect } from 'react';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import type { Company, PdfTemplateName } from '@/types/database';
import type { WeekStartsOn } from '@/lib/reportPeriod';
import { PdfTemplatePicker } from './PdfTemplatePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { QUOTE_PAYMENT_METHODS, normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { classifyPixKey } from '@/lib/pixQrCode';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import type { PaymentMethod } from '@/types/database';
import {
  Building2,
  FileText,
  Mail,
  Phone,
  MapPin,
  Palette,
  Image,
  Upload,
  Loader2,
  Crown,
  QrCode,
  Camera,
  PenLine,
  WifiOff
} from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { useCamera, dataUrlToFile } from '@/hooks/useCamera';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useAuth } from '@/contexts/AuthContext';
import { addToOfflineQueue } from '@/lib/offlineStorage';
import { cacheCompanies, getCachedCompanies } from '@/lib/offlineDataCache';
import { StorageImage } from '@/components/ui/StorageImage';
import { supabase } from '@/integrations/supabase/client';
import { CompanyFiscalSection, type FiscalSectionValue } from './CompanyFiscalSection';

interface CompanyFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company?: Company | null;
  onSuccess?: () => void;
}

export function CompanyFormModal({ 
  open, 
  onOpenChange, 
  company, 
  onSuccess 
}: CompanyFormModalProps) {
  const { user } = useAuth();
  const { createCompany, updateCompany, companies } = useCompany();
  const brandColor = useFeature('brand_color');
  const pdfTemplates = useFeature('pdf_templates');
  const documentSignature = useFeature('document_signature');
  const { isOnline } = useNetworkStatus();
  const isEditing = !!company;
  
  const [name, setName] = useState('');
  const [cnpjCpf, setCnpjCpf] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  // Assinatura salva do prestador (PRO): guardamos a URL do objeto no Storage,
  // nunca o data URL — a linha de `companies` é cacheada offline.
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureDraft, setSignatureDraft] = useState<string | null>(null);
  const [savingSignature, setSavingSignature] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#10B981');
  const [pixKey, setPixKey] = useState('');
  const [pixCity, setPixCity] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('');
  const [defaultPaymentMethods, setDefaultPaymentMethods] = useState<PaymentMethod[]>([]);
  const [weekStartsOn, setWeekStartsOn] = useState<WeekStartsOn>(0);
  // Padrão `true`: é o comportamento anterior à opção. Empresa nova nasce
  // imprimindo a linha, como todas as anteriores.
  const [pdvSignatureLine, setPdvSignatureLine] = useState(true);
  // Padrao `false`: nenhum documento anterior tem marca-d'agua.
  const [logoWatermark, setLogoWatermark] = useState(false);

  /**
   * No nível da EMPRESA, "nenhuma marcada" e "nunca configurei" são a mesma
   * coisa — por isso o estado local é sempre um array e o payload grava `null`
   * quando ele está vazio. A distinção `null` × `[]` que importa é a do
   * DOCUMENTO (ver `src/lib/quotePaymentInfo.ts`): lá, `[]` é a recusa
   * explícita que esconde o QR PIX.
   */
  const toggleDefaultPaymentMethod = (method: PaymentMethod) => {
    setDefaultPaymentMethods((prev) =>
      normalizePaymentMethods(
        prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
      ) ?? [],
    );
  };
  const [pdfTemplate, setPdfTemplate] = useState<PdfTemplateName>('classico');
  const [saving, setSaving] = useState(false);
  const [detectedPixKeyType, setDetectedPixKeyType] = useState<string | null>(null);
  // Dados fiscais (regime + município) reportados pela seção; persistidos no save.
  const [fiscal, setFiscal] = useState<FiscalSectionValue>({ taxpayerType: 'autonomo_informal', muni: null, ready: false });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { capturePhoto, isCapturing } = useCamera();
  const { uploadFile, uploadPhoto, isUploading: isUploadingLogo } = usePhotoUpload();
  // O rótulo vem de `classifyPixKey`, o MESMO classificador que monta o BR
  // Code — nunca de uma cópia local. Esta aqui devolvia "Outro" para texto
  // livre: a tela mostrava "✓ Tipo detectado: Outro" e o gerador produzia um
  // QR com chave vazia, que banco nenhum paga.
  const detectPixKeyType = (key: string): string | null => classifyPixKey(key).label;

  // Reset form when modal opens/closes or company changes
  useEffect(() => {
    // Zera o fiscal p/ 'não-pronto' a cada (re)abertura — a seção re-hidrata e
    // reporta de novo, evitando persistir dados fiscais da empresa anterior.
    setFiscal({ taxpayerType: 'autonomo_informal', muni: null, ready: false });
    if (open && company) {
      setName(company.name || '');
      setCnpjCpf(company.cnpj_cpf || '');
      setEmail(company.email || '');
      setPhone(company.phone || '');
      setAddress(company.address || '');
      setLogoUrl(company.logo_url || '');
      setSignatureUrl(company.signature_url || '');
      setPrimaryColor(company.primary_color || '#10B981');
      setPixKey(company.pix_key || '');
      setPixCity(company.pix_city || '');
      setDefaultTerms(company.default_terms || '');
      setDefaultPaymentMethods(normalizePaymentMethods(company.default_payment_methods) ?? []);
      setWeekStartsOn(company.week_starts_on === 1 ? 1 : 0);
      // `!== false` e não `Boolean(...)`: coluna ausente/indefinida (empresa
      // carregada de cache antigo) tem que cair no padrão LIGADO.
      setPdvSignatureLine(company.pdv_signature_line !== false);
      setLogoWatermark(company.logo_watermark === true);
      setPdfTemplate((company.pdf_template as PdfTemplateName) || 'classico');
      setDetectedPixKeyType(detectPixKeyType(company.pix_key || ''));
    } else if (open && !company) {
      // New company - reset form
      setName('');
      setCnpjCpf('');
      setEmail('');
      setPhone('');
      setAddress('');
      setLogoUrl('');
      setSignatureUrl('');
      setPrimaryColor('#10B981');
      setPixKey('');
      setPixCity('');
      setDefaultTerms('');
      setDefaultPaymentMethods([]);
      setWeekStartsOn(0);
      setPdfTemplate('classico');
      setDetectedPixKeyType(null);
    }
    setSignatureDraft(null);
    setSignatureOpen(false);
  }, [open, company]);

  // Use Capacitor Camera plugin
  const handleCameraUpload = async () => {
    const result = await capturePhoto({
      quality: 85,
      source: 'prompt', // Let user choose camera or gallery
    });

    if (!result?.dataUrl) return;

    if (!isOnline) {
      // Offline: keep the dataURL; NetworkContext will upload it on sync.
      setLogoUrl(result.dataUrl);
      toast.success('Logo salva localmente! Upload será feito ao sincronizar.', { icon: '📴' });
      return;
    }

    const uploaded = await uploadPhoto(result.dataUrl, 'public-app-assets', 'company-logos');
    if (uploaded) {
      setLogoUrl(uploaded.url);
      toast.success('Logo carregada!');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.warning('Por favor, selecione uma imagem válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.warning('A imagem deve ter no máximo 2MB');
      return;
    }

    if (!isOnline) {
      // Offline: fall back to dataURL so the form can be saved locally.
      // NetworkContext will upload it to Storage during sync.
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoUrl(e.target?.result as string);
        toast.success('Logo salva localmente! Upload será feito ao sincronizar.', { icon: '📴' });
      };
      reader.onerror = () => toast.error('Erro ao carregar a imagem');
      reader.readAsDataURL(file);
      return;
    }

    const uploaded = await uploadFile(file, 'public-app-assets', 'company-logos');
    if (uploaded) {
      setLogoUrl(uploaded.url);
      toast.success('Logo carregada!');
    }
  };
  
  // Handle color picker click for upsell
  const handleColorClick = () => {
    if (!brandColor.allowed) brandColor.require();
  };

  // Abre o quadro de assinatura (gated PRO). Requer conexão: diferente do logo,
  // não há caminho de upload-no-sync para a assinatura, e guardar o data URL na
  // linha de `companies` estouraria a cota do cache offline.
  const handleOpenSignature = () => {
    if (!documentSignature.allowed) {
      documentSignature.require();
      return;
    }
    if (!isOnline) {
      toast.warning('Sem conexão. Salvar a assinatura requer internet.');
      return;
    }
    setSignatureDraft(null);
    setSignatureOpen(true);
  };

  const handleSaveSignature = async () => {
    if (!signatureDraft) {
      toast.warning('Desenhe a assinatura antes de salvar.');
      return;
    }

    setSavingSignature(true);
    try {
      // O SignaturePad entrega PNG com fundo transparente — preservamos a
      // extensão para o Storage não rotular o objeto como JPEG.
      const file = dataUrlToFile(signatureDraft, 'assinatura.png');
      const uploaded = await uploadFile(file, 'public-app-assets', 'company-signatures');
      if (!uploaded) return; // uploadFile já avisou o usuário
      setSignatureUrl(uploaded.url);
      setSignatureOpen(false);
      setSignatureDraft(null);
      toast.success('Assinatura pronta! Salve a empresa para aplicá-la nos documentos.');
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error('Não foi possível enviar a assinatura. Tente novamente.');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleRemoveSignature = () => {
    if (!documentSignature.allowed) {
      documentSignature.require();
      return;
    }
    setSignatureUrl('');
  };

  // Persiste os dados fiscais (regime/CNPJ/município) no fiscal_profiles. O upload do
  // certificado (opcional) é o que leva o onboarding a 200; aqui deixamos em 102 quando
  // há CNPJ+município, e mantemos 200 se já estava concluído (não regride).
  const persistFiscalProfile = async (companyId: string) => {
    // Não persiste enquanto a seção não hidratou o perfil salvo (evita sobrescrever).
    if (!fiscal.ready) return;
    const cnpjD = cnpjCpf.replace(/\D/g, '');
    const emitter = fiscal.taxpayerType === 'mei' || fiscal.taxpayerType === 'me_epp';
    const { data: existing } = await supabase
      .from('fiscal_profiles').select('id, onboarding_status').eq('company_id', companyId).maybeSingle();

    if (!emitter) {
      // Registra a troca p/ autônomo só se já havia perfil; senão não cria nada.
      if (existing) {
        await supabase.from('fiscal_profiles').update({ taxpayer_type: 'autonomo_informal' }).eq('company_id', companyId);
      }
      return;
    }

    const nextStatus = existing?.onboarding_status === 200 ? 200 : 102;
    const common = {
      taxpayer_type: fiscal.taxpayerType,
      cnpj: cnpjD || null,
      municipio_ibge: fiscal.muni?.ibge ?? null,
      uf: fiscal.muni?.uf ?? null,
      provider: fiscal.muni?.provider ?? 'nacional',
      onboarding_status: nextStatus,
    };
    if (existing) {
      await supabase.from('fiscal_profiles').update(common).eq('company_id', companyId);
    } else {
      await supabase.from('fiscal_profiles').insert({ ...common, company_id: companyId, ambiente: 'homolog' });
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.warning('Nome da empresa é obrigatório');
      return;
    }

    const emitter = fiscal.ready && (fiscal.taxpayerType === 'mei' || fiscal.taxpayerType === 'me_epp');
    if (emitter && cnpjCpf.replace(/\D/g, '').length !== 14) {
      toast.warning('Informe um CNPJ válido (14 dígitos) para o regime selecionado.');
      return;
    }

    setSaving(true);

    const companyData = {
      name: name.trim(),
      cnpj_cpf: cnpjCpf.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      address: address.trim() || null,
      logo_url: logoUrl.trim() || null,
      // O gate PRO está nos handlers e no picker (o estado só muda por eles),
      // então aqui basta PERSISTIR. As três andam juntas de propósito: quem cai
      // para o grátis não tem a assinatura apagada, nem a cor da marca, nem o
      // modelo de PDF — salvar um dado sem relação (o telefone, por exemplo) não
      // pode destruir uma escolha em silêncio. Quem impede o grátis de IMPRIMIR
      // um modelo PRO é o `resolveTemplate` do `pdfGenerator`, na geração.
      signature_url: signatureUrl.trim() || null,
      primary_color: primaryColor,
      pix_key: pixKey.trim() || null,
      pix_city: pixCity.trim() || null,
      default_terms: defaultTerms.trim() || null,
      default_payment_methods: defaultPaymentMethods.length ? defaultPaymentMethods : null,
      week_starts_on: weekStartsOn,
      pdv_signature_line: pdvSignatureLine,
      logo_watermark: logoWatermark,
      pdf_template: pdfTemplate,
    };

    let success = false;
    let savedCompanyId: string | null = null;

    if (isEditing && company) {
      // Editing requires online
      if (!isOnline) {
        toast.warning('Sem conexão. Edição de empresa requer internet.');
        setSaving(false);
        return;
      }
      success = await updateCompany(company.id, companyData);
      if (success) savedCompanyId = company.id;
    } else {
      // Creating new company
      if (isOnline) {
        const newCompany = await createCompany(companyData);
        success = !!newCompany;
        savedCompanyId = newCompany?.id ?? null;
      } else {
        // Offline company creation - queue and cache locally
        if (!user) {
          toast.warning('Você precisa estar logado');
          setSaving(false);
          return;
        }

        // Add to offline queue first so we can reuse the operation id as the
        // temporary company id. This is what lets the id-map / chain-cleanup
        // machinery in NetworkContext line up mutations to this record.
        const tempId = addToOfflineQueue('create_company', { ...companyData, user_id: user.id });

        const offlineCompany: Company = {
          id: tempId,
          user_id: user.id,
          created_at: new Date().toISOString(),
          ...companyData,
          _offline_pending: true,
        } as Company & { _offline_pending: boolean };

        // Update local cache
        const cachedCompanies = getCachedCompanies(user.id) || [];
        cacheCompanies(user.id, [...cachedCompanies, offlineCompany]);

        // Dispatch event to refresh company list
        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: tempId } }));

        toast.success('Empresa criada offline! Será sincronizada quando houver internet.');
        success = true;
      }
    }

    // Dados fiscais (online + empresa com id). Best-effort: não desfaz o cadastro.
    if (success && savedCompanyId && isOnline) {
      try { await persistFiscalProfile(savedCompanyId); }
      catch { toast.warning('Empresa salva, mas houve um problema ao salvar os dados fiscais.'); }
    }

    setSaving(false);

    if (success) {
      onOpenChange(false);
      onSuccess?.();
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            {isEditing ? 'Editar empresa' : 'Nova empresa'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Logo */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Image className="h-4 w-4" />
              Logo
            </Label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-muted overflow-hidden">
                {logoUrl ? (
                  <StorageImage
                    src={logoUrl}
                    alt="Logo"
                    className="h-14 w-14 object-cover"
                    fallback={<Image className="h-6 w-6 text-muted-foreground" />}
                  />
                ) : (
                  <Image className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex-1 flex gap-2">
                <Input
                  placeholder="https://... ou faça upload"
                  value={logoUrl.startsWith('data:') ? '' : logoUrl}
                  onChange={(e) => setLogoUrl(e.target.value)}
                  className="flex-1"
                />
                {/* Hidden file input for web fallback */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="logo-upload"
                />
                {/* Camera button using Capacitor Camera plugin */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCameraUpload}
                  disabled={isCapturing || isUploadingLogo}
                  aria-label="Capturar ou escolher imagem"
                >
                  {isCapturing || isUploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                </Button>
                {/* Fallback upload button */}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  aria-label="Fazer upload de imagem"
                >
                  {isUploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            {logoUrl.startsWith('data:') && (
              <p className="text-xs text-muted-foreground">
                ✓ Imagem carregada do dispositivo
              </p>
            )}
          </div>

          {/* Assinatura do prestador — desenhada uma vez, impressa em todos os
              orçamentos, pedidos e ordens de serviço. Não vale para contrato. */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <PenLine className="h-4 w-4" />
              Assinatura
              {!documentSignature.allowed && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  PRO
                </span>
              )}
            </Label>
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg border bg-white overflow-hidden">
                {signatureUrl ? (
                  <StorageImage
                    src={signatureUrl}
                    alt="Assinatura salva"
                    className="h-14 w-24 object-contain"
                    fallback={<PenLine className="h-6 w-6 text-muted-foreground" />}
                  />
                ) : (
                  <PenLine className="h-6 w-6 text-muted-foreground" />
                )}
              </div>
              <div className="flex flex-1 flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleOpenSignature}
                  disabled={isUploadingLogo}
                >
                  {/* "Refazer assinatura" estoura a linha em 360px (337px
                      necessários para 312 úteis) e joga "Remover" p/ baixo. */}
                  {signatureUrl ? 'Refazer' : 'Assinar'}
                </Button>
                {signatureUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemoveSignature}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    Remover
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Aparece acima de "Assinatura do responsável" nos orçamentos, pedidos e
              ordens de serviço. Contratos continuam exigindo assinatura na hora.
            </p>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Nome da empresa *
            </Label>
            <Input
              placeholder="Minha Empresa LTDA"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* CNPJ/CPF */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              CNPJ / CPF
            </Label>
            <Input
              placeholder="00.000.000/0000-00"
              value={cnpjCpf}
              onChange={(e) => setCnpjCpf(e.target.value)}
            />
          </div>

          {/* Dados fiscais (NFS-e) — regime + município + certificado */}
          <div className="space-y-3 rounded-lg border p-3">
            <Label className="flex items-center gap-2 text-sm font-semibold">
              <FileText className="h-4 w-4 text-primary" />
              Dados fiscais (NFS-e)
            </Label>
            <CompanyFiscalSection
              companyId={company?.id ?? null}
              isOwner={true}
              cnpjDigits={cnpjCpf.replace(/\D/g, '')}
              pixCity={pixCity}
              address={address}
              onChange={setFiscal}
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              E-mail comercial
            </Label>
            <Input
              type="email"
              placeholder="contato@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Telefone / WhatsApp
            </Label>
            <Input
              placeholder="(00) 00000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Endereço
            </Label>
            <Input
              placeholder="Rua Exemplo, 123 - Cidade/UF"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          {/* Pix Key */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              Chave Pix
            </Label>
            <Input
              placeholder="sua.chave@pix.com"
              value={pixKey}
              onChange={(e) => {
                setPixKey(e.target.value);
                setDetectedPixKeyType(detectPixKeyType(e.target.value));
              }}
            />
            {detectedPixKeyType ? (
              <p className="text-xs text-primary font-medium">
                ✓ Tipo detectado: {detectedPixKeyType}
              </p>
            ) : pixKey.trim() ? (
              // Aviso, não erro (#26): nada falhou, e é o usuário quem resolve
              // digitando a chave certa. Ele diz o que ACONTECE, porque a
              // consequência (documento sem QR) é invisível até o cliente
              // reclamar.
              // `text-amber-600`, e NÃO `text-warning-foreground`: esse token é
              // branco puro, feito para ficar SOBRE o âmbar. Solto no cartão, o
              // aviso sairia branco no branco.
              <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                Não reconhecemos esse formato de chave Pix. Use e-mail, celular, CPF, CNPJ ou chave
                aleatória — senão o orçamento sai sem QR Code.
              </p>
            ) : null}
            <p className="text-xs text-muted-foreground">
              Será exibida no PDF com QR Code para facilitar o pagamento.
            </p>
          </div>

          {/* Pix City */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Cidade do PIX
            </Label>
            <Input
              placeholder="São Paulo"
              value={pixCity}
              onChange={(e) => setPixCity(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Cidade para o QR Code PIX. Se vazio, usa "São Paulo" como padrão.
            </p>
          </div>

          {/* Default Terms */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Termos e condições padrão
            </Label>
            <textarea
              placeholder="Ex: O prazo de entrega é de 10 dias úteis após a aprovação..."
              value={defaultTerms}
              onChange={(e) => setDefaultTerms(e.target.value)}
              maxLength={5000}
              className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground">
              Este texto será preenchido automaticamente ao criar um novo orçamento.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="week-start">Início da semana</Label>
            <select
              id="week-start"
              value={weekStartsOn}
              onChange={(event) => setWeekStartsOn(event.target.value === '1' ? 1 : 0)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value={0}>Domingo</option>
              <option value={1}>Segunda-feira</option>
            </select>
            <p className="text-xs text-muted-foreground">
              Vale igualmente para “Esta semana” nos relatórios, Agenda e Dashboard.
            </p>
          </div>

          {/* Assinatura do cliente no PDV. Fica junto das preferências de
              documento porque governa o que sai impresso, no A4 e no cupom. */}
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              {/* Checkbox + div irmã: <label> em volta do Radix alterna 2x (armadilha #29). */}
              <Checkbox
                id="pdv-signature-line"
                checked={pdvSignatureLine}
                onCheckedChange={(v) => setPdvSignatureLine(v === true)}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                className="cursor-pointer text-sm leading-tight"
                onClick={() => setPdvSignatureLine(!pdvSignatureLine)}
              >
                Linha de assinatura do cliente na venda rápida
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Desmarque se a venda de balcão já sai paga e você não pede assinatura.
              Vale para o PDF e para o cupom térmico. A linha do responsável continua.
            </p>
          </div>

          {/* Marca-d'água da logo. Fica junto da assinatura do PDV: as duas
              governam o que sai impresso no documento. */}
          <div className="space-y-2">
            <div className="flex items-start gap-2">
              <Checkbox
                id="logo-watermark"
                checked={logoWatermark}
                onCheckedChange={(v) => setLogoWatermark(v === true)}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                className="cursor-pointer text-sm leading-tight"
                onClick={() => setLogoWatermark(!logoWatermark)}
              >
                Logo como marca-d'água no PDF
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Imprime a logo bem clara atrás do conteúdo. Precisa de logo cadastrada
              e não vale para o cupom térmico, que imprime só em preto e branco.
            </p>
          </div>

          {/* Formas de pagamento aceitas (padrão) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Formas de pagamento aceitas (padrão)
            </Label>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {QUOTE_PAYMENT_METHODS.map((method) => {
                const checked = defaultPaymentMethods.includes(method);
                return (
                  <div key={method} className="flex items-center gap-2">
                    {/* Checkbox + div irmã: <label> em volta do Radix alterna 2x (armadilha #29). */}
                    <Checkbox
                      id={`default-payment-${method}`}
                      checked={checked}
                      onCheckedChange={() => toggleDefaultPaymentMethod(method)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      className="cursor-pointer text-sm leading-tight"
                      onClick={() => toggleDefaultPaymentMethod(method)}
                    >
                      {PAYMENT_METHOD_LABELS[method]}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Vem pré-selecionado em cada orçamento novo, e continua editável lá. Sem
              nenhuma marcada, um orçamento novo só marca PIX se você tiver chave
              cadastrada acima.
            </p>
          </div>

          {/* PDF Template - with upsell for non-PRO */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Modelo do PDF
              {!pdfTemplates.allowed && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  PRO
                </span>
              )}
            </Label>
            <PdfTemplatePicker
              value={pdfTemplate}
              onChange={setPdfTemplate}
              accent={primaryColor}
            />
            <p className="text-xs text-muted-foreground">
              É o modelo PADRÃO: todo orçamento novo nasce com ele, e dá para
              trocar em cada orçamento na própria tela.
            </p>
          </div>

          {/* Primary Color - with upsell for non-PRO */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              Cor principal
              {!brandColor.allowed && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <Crown className="h-3 w-3" />
                  PRO
                </span>
              )}
            </Label>
            <div className="flex items-center gap-3">
              <div className="relative" onClick={handleColorClick}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => {
                    if (brandColor.allowed) {
                      setPrimaryColor(e.target.value.toUpperCase());
                    }
                  }}
                  onClick={(e) => {
                    if (!brandColor.allowed) {
                      e.preventDefault();
                      handleColorClick();
                    }
                  }}
                  className={`h-10 w-16 rounded-lg border cursor-pointer ${!brandColor.allowed ? 'cursor-pointer' : ''}`}
                  style={{ pointerEvents: brandColor.allowed ? 'auto' : 'none' }}
                />
                {!brandColor.allowed && (
                  <div 
                    className="absolute inset-0 cursor-pointer" 
                    onClick={handleColorClick}
                  />
                )}
              </div>
              <Input
                value={primaryColor}
                onChange={(e) => {
                  if (!brandColor.allowed) {
                    handleColorClick();
                    return;
                  }
                  let value = e.target.value.toUpperCase();
                  if (value && !value.startsWith('#')) {
                    value = '#' + value;
                  }
                  if (value.length <= 7) {
                    setPrimaryColor(value);
                  }
                }}
                onClick={() => {
                  if (!brandColor.allowed) handleColorClick();
                }}
                placeholder="#10B981"
                className={`w-24 font-mono text-sm cursor-pointer`}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button3D color="gray" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleSubmit} disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Criar empresa'
            )}
          </Button3D>
        </DialogFooter>
      </DialogContent>

    </Dialog>

    {/* Quadro de assinatura do prestador. O upload acontece aqui; a coluna
        `signature_url` só é gravada quando a empresa for salva. */}
    <Dialog open={signatureOpen} onOpenChange={setSignatureOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-primary" />
            Sua assinatura
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <SignaturePad label="Assine no quadro abaixo" onSignatureChange={setSignatureDraft} />
          <p className="text-xs text-muted-foreground">
            Ela será impressa acima da linha "Assinatura do responsável". Você pode
            refazê-la quando quiser.
          </p>
        </div>
        <DialogFooter>
          {/* Cancelar NUNCA é desabilitado junto com o confirmar: se o upload
              travar (rede ruim, WebView congelada), o usuário ficaria preso no
              modal sem saída. Fechar durante o envio é seguro — o setState do
              `finally` vira no-op. */}
          <Button3D color="gray" onClick={() => setSignatureOpen(false)}>
            Cancelar
          </Button3D>
          <Button3D onClick={handleSaveSignature} disabled={savingSignature || !signatureDraft}>
            {savingSignature ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              'Usar esta assinatura'
            )}
          </Button3D>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
